import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Load from './Load';
import Pagination from './Pagination';
import {
  TORNEIOS_POR_ATLETA,
  TorneiosPorAtletaData,
  TorneiosPorAtletaVars,
} from '../services/torneioService';

interface AtletaTorneiosTabProps {
  atletaId: string;
}

const PAGE_SIZE = 15;

const formatTorneioData = (
  dataInicio?: string | null,
  dataFim?: string | null,
): string => {
  if (!dataInicio) {
    return '';
  }

  const [year, month, day] = dataInicio.split('-').map(Number);
  const inicio = new Date(year, month - 1, day);
  const inicioStr = inicio.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });

  if (dataFim) {
    const [yf, mf, df] = dataFim.split('-').map(Number);
    const fim = new Date(yf, mf - 1, df);
    const fimStr = fim.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${inicioStr} - ${fimStr}`;
  }

  return inicio.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const AtletaTorneiosTab: React.FC<AtletaTorneiosTabProps> = ({ atletaId }) => {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<
    TorneiosPorAtletaData,
    TorneiosPorAtletaVars
  >(TORNEIOS_POR_ATLETA, {
    variables: { atletaId, page, size: PAGE_SIZE },
    skip: !atletaId,
  });

  if (loading) {
    return <Load />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Erro ao carregar torneios.
      </div>
    );
  }

  const items = data?.torneiosPorAtletaId.items ?? [];
  const totalPages = data?.torneiosPorAtletaId.totalPages ?? 0;

  if (items.length === 0) {
    return (
      <div>
        <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
          <div className="card-body text-center">
            <p className="mb-0">Nenhum torneio encontrado.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row g-3">
        {items.map((torneio) => {
          const cidade = torneio.local?.endereco?.cidade;
          const localStr = cidade
            ? `${cidade.nome}, ${cidade.uf}`
            : torneio.local?.nome || '';
          const datasStr = formatTorneioData(torneio.dataInicio, torneio.dataFim);

          return (
            <div key={torneio.id} className="col-12 col-sm-6 col-lg-4">
              <div
                className="card w-100 shadow-xss rounded-xxl border-0 p-3 hover-card"
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/tournamentsingle/${torneio.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/tournamentsingle/${torneio.id}`);
                  }
                }}
              >
                <div className="d-flex align-items-center">
                  <figure className="avatar me-3 mb-0">
                    <img
                      src={torneio.avatarUrl || '/assets/images/avatar.jpg'}
                      alt={torneio.nome}
                      className="rounded-circle"
                      style={{ width: 48, height: 48, objectFit: 'cover' }}
                    />
                  </figure>
                  <div className="flex-grow-1 min-w-0 text-truncate">
                    <div className="fw-600 text-grey-900 text-truncate">
                      {torneio.nome}
                    </div>
                    <div className="font-xssss text-grey-500">
                      {datasStr}
                    </div>
                    {localStr && (
                      <div className="font-xssss text-grey-500 text-truncate">
                        {localStr}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default AtletaTorneiosTab;
